import React, { createContext, useContext, useMemo } from 'react';
import ufovSymbolSets from '../lib/ufovSymbolSets';

const {
    DEFAULT_SYMBOL_VARIANT_ID,
    getUFOVSymbolDisplayName,
    getUFOVSymbolRecord,
    getUFOVSymbolVariant,
} = ufovSymbolSets;

const UFOVSymbolVariantContext = createContext(getUFOVSymbolVariant(DEFAULT_SYMBOL_VARIANT_ID));

export function UFOVSymbolVariantProvider({ children, variantId = DEFAULT_SYMBOL_VARIANT_ID }) {
    const variant = useMemo(() => getUFOVSymbolVariant(variantId), [variantId]);

    return (
        <UFOVSymbolVariantContext.Provider value={variant}>
            {children}
        </UFOVSymbolVariantContext.Provider>
    );
}

export function useUFOVSymbolVariant() {
    return useContext(UFOVSymbolVariantContext);
}

function getSpriteSheetStyle(atlas, sprite) {
    const atlasWidth = Number(atlas?.width) || 1;
    const atlasHeight = Number(atlas?.height) || 1;
    const spriteWidth = Number(sprite?.width) || atlasWidth;
    const spriteHeight = Number(sprite?.height) || atlasHeight;
    const spriteX = Number(sprite?.x) || 0;
    const spriteY = Number(sprite?.y) || 0;

    return {
        width: `${(atlasWidth / spriteWidth) * 100}%`,
        height: `${(atlasHeight / spriteHeight) * 100}%`,
        left: `-${(spriteX / spriteWidth) * 100}%`,
        top: `-${(spriteY / spriteHeight) * 100}%`,
    };
}

export function UFOVSymbol({
    symbol,
    className = '',
    decorative = true,
    label = '',
    usage = 'default',
}) {
    const variant = useUFOVSymbolVariant();
    const record = getUFOVSymbolRecord(variant.id, symbol);
    const accessibleLabel = label || getUFOVSymbolDisplayName(variant.id, symbol);
    const resolvedClassName = `UFOV-game__symbol UFOV-game__symbol--image ${className}`.trim();

    if (!record) {
        return <span aria-hidden="true" className={`${resolvedClassName} UFOV-game__symbol--fallback`}>?</span>;
    }

    const symbolAssetPath = record.symbolAssetPath || '';
    const assetPath = record.assetPath || '';
    const atlasPath = variant.atlas?.path || '';
    const spriteStyle = record.sprite && atlasPath
        ? getSpriteSheetStyle(variant.atlas, record.sprite)
        : null;

    if (symbolAssetPath) {
        return (
            <span
                aria-hidden={decorative ? 'true' : undefined}
                aria-label={decorative ? undefined : accessibleLabel}
                className={resolvedClassName}
                role={decorative ? undefined : 'img'}
            >
                <img
                    aria-hidden="true"
                    alt=""
                    className="UFOV-game__symbol-img"
                    decoding="sync"
                    draggable="false"
                    fetchpriority="high"
                    loading="eager"
                    src={symbolAssetPath}
                />
            </span>
        );
    }

    if (spriteStyle && atlasPath) {
        return (
            <span
                aria-hidden={decorative ? 'true' : undefined}
                aria-label={decorative ? undefined : accessibleLabel}
                className={resolvedClassName}
                role={decorative ? undefined : 'img'}
            >
                <span className="UFOV-game__symbol-sprite-frame">
                    <img
                        aria-hidden="true"
                        alt=""
                        className="UFOV-game__symbol-sprite-sheet"
                        decoding="async"
                        draggable="false"
                        src={atlasPath}
                        style={spriteStyle}
                    />
                </span>
            </span>
        );
    }

    if (assetPath) {
        return (
            <span
                aria-hidden={decorative ? 'true' : undefined}
                aria-label={decorative ? undefined : accessibleLabel}
                className={resolvedClassName}
                role={decorative ? undefined : 'img'}
            >
                <img
                    aria-hidden="true"
                    alt=""
                    className="UFOV-game__symbol-img"
                    decoding="async"
                    draggable="false"
                    src={assetPath}
                />
            </span>
        );
    }

    return (
        <span
            aria-hidden="true"
            className={`${resolvedClassName} UFOV-game__symbol--fallback`}
        >
            ?
        </span>
    );
}

export default UFOVSymbol;
